import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
  minDate?: string;
}

const DatePicker = ({
  value,
  onChange,
  disabled = false,
  placeholder = 'Select date',
  required = false,
  minDate,
}: DatePickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Initialize selected date from value
  useEffect(() => {
    if (value) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        setSelectedDate(date);
        setCurrentMonth(date.getMonth());
        setCurrentYear(date.getFullYear());
      }
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Get days in month
  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Get first day of month (0 = Sunday, 1 = Monday, etc.)
  const getFirstDayOfMonth = (month: number, year: number) => {
    return new Date(year, month, 1).getDay();
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  // Check if date is disabled (before minDate)
  const isDateDisabled = (day: number) => {
    if (!minDate) return false;

    const date = new Date(currentYear, currentMonth, day);
    const min = new Date(minDate);
    return date < min;
  };

  // Check if date is today
  const isToday = (day: number) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentMonth === today.getMonth() &&
      currentYear === today.getFullYear()
    );
  };

  // Check if date is selected
  const isSelected = (day: number) => {
    if (!selectedDate) return false;
    return (
      day === selectedDate.getDate() &&
      currentMonth === selectedDate.getMonth() &&
      currentYear === selectedDate.getFullYear()
    );
  };

  // Handle date selection
  const handleDateSelect = (day: number) => {
    if (isDateDisabled(day)) return;

    const date = new Date(currentYear, currentMonth, day);
    setSelectedDate(date);

    // Format date as YYYY-MM-DD
    const formattedDate = date.toISOString().split('T')[0];
    onChange(formattedDate);
    setIsOpen(false);
  };

  // Navigate to previous month
  const goToPreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  // Navigate to next month
  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  // Format display date
  const formatDisplayDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const calendarDays = generateCalendarDays();

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Date Input Button */}
      <button
        type="button"
        onClick={toggleDropdown}
        disabled={disabled}
        className={`w-full flex items-center justify-between px-4 py-3 border border-neutral-300 rounded-md bg-white text-left focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 ${
          disabled
            ? 'opacity-50 cursor-not-allowed bg-neutral-50'
            : 'hover:border-red-300 cursor-pointer'
        } ${isOpen ? 'ring-2 ring-red-500 border-red-500' : ''}`}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label="Select date"
        required={required}
      >
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-neutral-500" />
          <span
            className={`text-sm sm:text-base ${
              selectedDate ? 'text-neutral-900' : 'text-neutral-500'
            }`}
          >
            {selectedDate ? formatDisplayDate(selectedDate) : placeholder}
          </span>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-neutral-500 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Calendar Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="absolute top-full left-0 right-0 mt-2 bg-white border border-neutral-300 rounded-lg shadow-xl z-[99999] overflow-hidden min-w-[280px] sm:min-w-[320px] date-picker-dropdown"
            role="dialog"
            aria-label="Calendar"
          >
            {/* Calendar Header */}
            <div className="px-4 py-3 bg-gradient-to-r from-red-800 to-red-700 text-white">
              <div className="flex items-center justify-between">
                <motion.button
                  type="button"
                  onClick={goToPreviousMonth}
                  className="p-1 rounded-full hover:bg-white/20 transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label="Previous month"
                >
                  <ChevronLeft className="w-5 h-5" />
                </motion.button>

                <motion.div
                  key={`${currentMonth}-${currentYear}`}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className="text-center"
                >
                  <h3 className="font-semibold text-lg">
                    {months[currentMonth]} {currentYear}
                  </h3>
                </motion.div>

                <motion.button
                  type="button"
                  onClick={goToNextMonth}
                  className="p-1 rounded-full hover:bg-white/20 transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label="Next month"
                >
                  <ChevronRight className="w-5 h-5" />
                </motion.button>
              </div>
            </div>

            {/* Calendar Body */}
            <div className="p-4">
              {/* Weekday Headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {weekdays.map(day => (
                  <div
                    key={day}
                    className="text-center text-[10px] sm:text-xs font-medium text-neutral-500 py-1 sm:py-2"
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <motion.div
                key={`${currentMonth}-${currentYear}`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
                className="grid grid-cols-7 gap-1"
              >
                {calendarDays.map((day, index) => (
                  <div key={index} className="aspect-square">
                    {day && (
                      <motion.button
                        type="button"
                        onClick={() => handleDateSelect(day)}
                        disabled={isDateDisabled(day)}
                        className={`w-full h-full flex items-center justify-center text-sm font-medium rounded-lg transition-all duration-200 ${
                          isSelected(day)
                            ? 'bg-red-800 text-white shadow-md'
                            : isToday(day)
                              ? 'bg-red-100 text-red-800 border-2 border-red-300'
                              : isDateDisabled(day)
                                ? 'text-neutral-300 cursor-not-allowed'
                                : 'text-neutral-700 hover:bg-red-50 hover:text-red-800'
                        }`}
                        whileHover={
                          !isDateDisabled(day)
                            ? {
                                scale: isSelected(day) ? 1 : 1.1,
                                transition: { duration: 0.1 },
                              }
                            : {}
                        }
                        whileTap={!isDateDisabled(day) ? { scale: 0.95 } : {}}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{
                          opacity: 1,
                          scale: 1,
                          transition: {
                            delay: index * 0.01,
                            duration: 0.2,
                          },
                        }}
                        aria-label={`${day} ${months[currentMonth]} ${currentYear}`}
                        aria-selected={isSelected(day)}
                      >
                        {day}
                        {isToday(day) && !isSelected(day) && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="absolute w-1 h-1 bg-red-600 rounded-full mt-6"
                          />
                        )}
                      </motion.button>
                    )}
                  </div>
                ))}
              </motion.div>
            </div>

            {/* Calendar Footer */}
            <div className="px-4 py-3 bg-neutral-50 border-t border-neutral-200">
              <div className="flex items-center justify-between text-xs text-neutral-500">
                <span>Today: {new Date().toLocaleDateString()}</span>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-red-100 border border-red-300 rounded-full"></div>
                    <span>Today</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-red-800 rounded-full"></div>
                    <span>Selected</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DatePicker;
